if (!customElements.get('vr-client-videos')) {
  customElements.define('vr-client-videos', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.rail = this.querySelector('[data-client-rail]');
      if (!this.rail) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.videos = Array.from(this.querySelectorAll('[data-client-video]'));
      this.requests = new WeakMap();
      this.pending = new WeakMap();
      this.failed = new Set();
      this.index = 0;
      this.inView = false;
      this.motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.autoPaused = Boolean(this.motion?.matches);
      this.previous = this.querySelector('[data-client-previous]');
      this.next = this.querySelector('[data-client-next]');
      this.all = this.querySelector('[data-client-all]');
      this.auto = this.querySelector('[data-client-auto]');
      this.setAttribute('data-enhanced', '');
      this.videos.forEach((video) => {
        const card = video.closest('[data-client-card]');
        const button = card.querySelector('[data-client-play]');
        video.dataset.autoplay = 'true';
        video.controls = false;
        video.muted = true;
        video.playsInline = true;
        video.loop = false;
        card.removeAttribute('data-native-controls');
        button.hidden = false;
        button.addEventListener('click', () => this.playVideo(video), { signal });
        video.addEventListener('play', () => {
          if (!this.controller || this.videos[this.index] !== video || document.hidden
            || (video.dataset.clientPlayback === 'automatic' && !this.shouldPlay())) {
            video.pause();
            return;
          }
          this.pauseOthers(video);
          button.hidden = true;
          card.querySelector('[data-client-error]').hidden = true;
        }, { signal });
        video.addEventListener('pause', () => {
          button.hidden = false;
          if (!this.internalPause && !video.ended && this.videos[this.index] === video && this.shouldPlay()) {
            this.autoPaused = true;
            this.updateAuto();
          }
        }, { signal });
        video.addEventListener('ended', () => {
          button.hidden = false;
          if (this.videos[this.index] === video && this.shouldPlay()) this.scrollRail(1);
        }, { signal });
        video.addEventListener('error', () => {
          this.failed.add(video);
          this.showError(video);
          if (this.videos[this.index] === video && this.shouldPlay()) this.scrollRail(1);
        }, { signal });
      });
      this.previous.hidden = false;
      this.next.hidden = false;
      this.all.hidden = false;
      if (this.auto) {
        this.auto.hidden = false;
        this.auto.addEventListener('click', () => {
          this.autoPaused = !this.autoPaused;
          this.updateAuto();
          if (this.autoPaused) this.pauseOthers();
          else this.resume();
        }, { signal });
      }
      this.previous.addEventListener('click', () => this.scrollRail(-1), { signal });
      this.next.addEventListener('click', () => this.scrollRail(1), { signal });
      this.all.addEventListener('click', () => this.toggleAll(), { signal });
      this.rail.addEventListener('keydown', (event) => {
        if (event.target !== this.rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.scrollRail(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseOthers();
        else this.resume();
      }, { signal });
      this.motion?.addEventListener?.('change', (event) => {
        if (!event.matches) return;
        this.autoPaused = true;
        this.updateAuto();
        this.pauseOthers();
      }, { signal });
      if (typeof IntersectionObserver !== 'undefined') {
        this.viewportObserver = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
          if (this.inView) this.resume();
          else this.pauseOthers();
        }, { threshold: [0, 0.15] });
        this.viewportObserver.observe(this);
      }
      let railWidth = this.rail.clientWidth;
      window.addEventListener('resize', () => {
        // Ignore toolbar/keyboard resizes on mobile; only re-align when the rail width changes.
        if (this.rail.clientWidth === railWidth) return;
        railWidth = this.rail.clientWidth;
        this.reveal();
      }, { signal });
      this.updateAuto();
      this.updateArrows();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.inView = false;
      this.viewportObserver?.disconnect();
      this.pauseOthers();
      this.removeAttribute('data-enhanced');
      this.videos?.forEach((video) => {
        video.controls = true;
        video.closest('[data-client-card]').querySelector('[data-client-play]').hidden = true;
      });
    }

    shouldPlay() {
      return Boolean(this.controller && this.isConnected && this.inView && !document.hidden && !this.autoPaused);
    }

    pauseOthers(keep) {
      this.internalPause = true;
      this.videos?.forEach((video) => {
        if (video === keep) return;
        this.requests.delete(video);
        this.pending.delete(video);
        video.closest('[data-client-card]').querySelector('[data-client-play]').disabled = false;
        if (!video.paused) video.pause();
      });
      this.internalPause = false;
    }

    resume() {
      const video = this.videos[this.index];
      if (this.shouldPlay() && video && video.paused && !this.pending.has(video)) this.playVideo(video, true);
    }

    async playVideo(video, automatic = false) {
      if (automatic && !this.shouldPlay()) return;
      this.index = this.videos.indexOf(video);
      if (this.index < 0) return;
      const card = video.closest('[data-client-card]');
      const button = card.querySelector('[data-client-play]');
      const request = {};
      this.requests.set(video, request);
      this.pending.set(video, request);
      this.failed.delete(video);
      this.pauseOthers(video);
      card.querySelector('[data-client-error]').hidden = true;
      card.toggleAttribute('data-native-controls', !automatic);
      video.controls = !automatic;
      video.dataset.clientPlayback = automatic ? 'automatic' : 'manual';
      if (automatic) video.muted = true;
      button.disabled = true;
      try {
        await video.play();
        if (this.requests.get(video) !== request) {
          if (!this.requests.has(video)) video.pause();
          return;
        }
        if (!this.controller || this.videos[this.index] !== video || document.hidden
          || (automatic && !this.shouldPlay())) video.pause();
      } catch (error) {
        if (this.controller && this.requests.get(video) === request) {
          if (automatic) { this.autoPaused = true; this.updateAuto(); }
          this.showError(video);
        }
      } finally {
        if (this.pending.get(video) === request) this.pending.delete(video);
        if (this.requests.get(video) === request) button.disabled = false;
      }
    }

    showError(video) {
      const card = video.closest('[data-client-card]');
      const message = card.querySelector('[data-client-error]');
      message.textContent = 'No pudimos reproducir este vídeo. Inténtalo de nuevo o usa sus controles.';
      message.hidden = false;
      video.controls = true;
      card.setAttribute('data-native-controls', '');
      const button = card.querySelector('[data-client-play]');
      button.hidden = false;
      button.disabled = false;
    }

    scrollRail(direction) {
      let next = this.index;
      let remaining = this.videos.length;
      do { next = (next + direction + this.videos.length) % this.videos.length; }
      while (this.failed.has(this.videos[next]) && --remaining > 0);
      if (!remaining) { this.autoPaused = true; this.pauseOthers(); this.updateAuto(); return; }
      this.index = next;
      this.pauseOthers();
      this.videos[next].currentTime = 0;
      this.reveal();
      this.resume();
    }

    reveal() {
      if (this.rail.hasAttribute('data-expanded')) return;
      const cards = this.querySelectorAll('[data-client-card]');
      this.rail.scrollTo?.({ left: cards[this.index].offsetLeft - cards[0].offsetLeft,
        behavior: this.motion?.matches ? 'instant' : 'smooth' });
    }

    toggleAll() {
      const expanded = this.rail.toggleAttribute('data-expanded');
      this.all.setAttribute('aria-expanded', String(expanded));
      this.all.querySelector('[data-client-all-text]').textContent = expanded ? 'Ver menos' : 'Ver todos';
      this.updateArrows();
      if (!expanded) this.reveal();
    }

    updateAuto() {
      if (!this.auto) return;
      this.auto.setAttribute('aria-pressed', String(this.autoPaused));
      this.auto.setAttribute('aria-label', this.autoPaused ? 'Reanudar vídeos automáticos' : 'Pausar vídeos automáticos');
    }

    updateArrows() {
      const expanded = this.rail.hasAttribute('data-expanded');
      this.previous.hidden = expanded;
      this.next.hidden = expanded;
      this.previous.disabled = this.videos.length < 2;
      this.next.disabled = this.videos.length < 2;
    }
  });
}
